import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { Card } from '../../layout/Card.jsx';
import { useDashboard } from './hooks/useDashboard.js';
import { KpiGrid } from './components/KpiGrid.jsx';
import { HourlyRevenueChart } from './components/HourlyRevenueChart.jsx';
import { RevenueDonut } from './components/RevenueDonut.jsx';
import { LatestInvoicesTable } from './components/LatestInvoicesTable.jsx';
import { TopSpendersList } from './components/TopSpendersList.jsx';
import { AlertsPanel } from './components/AlertsPanel.jsx';
import { PendingInvoicesPanel } from './components/PendingInvoicesPanel.jsx';

const RANGES = [
  { key: 'today', label: 'اليوم' },
  { key: 'yesterday', label: 'أمس' },
  { key: 'week', label: 'الأسبوع' },
  { key: 'month', label: 'الشهر' }
];

function rangeFor(key) {
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  if (key === 'today') return { from: iso(today), to: iso(today) };
  if (key === 'yesterday') {
    const y = new Date(today);
    y.setDate(y.getDate() - 1);
    return { from: iso(y), to: iso(y) };
  }
  if (key === 'week') {
    const w = new Date(today);
    w.setDate(w.getDate() - 6);
    return { from: iso(w), to: iso(today) };
  }
  if (key === 'month') {
    const m = new Date(today);
    m.setDate(m.getDate() - 29);
    return { from: iso(m), to: iso(today) };
  }
  return { from: iso(today), to: iso(today) };
}

function RangePicker({ value, onChange }) {
  return (
    <div className="inline-flex items-center gap-1 p-1 bg-white border border-line rounded-[10px] shadow-card">
      {RANGES.map((r) => (
        <button
          key={r.key}
          type="button"
          onClick={() => onChange(r.key)}
          className={clsx(
            'px-3 py-1.5 rounded-[8px] text-[12px] font-semibold transition-colors',
            value === r.key
              ? 'bg-orange text-white shadow-orange-glow'
              : 'text-ink-2 hover:bg-paper-2'
          )}
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}

export function Dashboard() {
  const [rangeKey, setRangeKey] = useState('today');
  const range = useMemo(() => rangeFor(rangeKey), [rangeKey]);

  const {
    overview,
    hourly,
    donut,
    activeSessions,
    pendingInvoices,
    latestInvoices,
    lowStock,
    topSpenders
  } = useDashboard(range);

  // نص فرعي صادق: المدى المختار وعدد أيامه كما احتسبها الخادم.
  const hourlySub = range.from === range.to
    ? `${range.to} — توزيع الإيراد على ساعات اليوم`
    : `${range.from} ← ${range.to} — مجموع ${hourly.data?.days ?? ''} يوماً على ساعات اليوم`;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-extrabold tracking-tight m-0">اللوحة الرئيسية</h2>
          <div className="text-ink-3 text-xs mt-0.5">نظرة عامة على نشاط القاعة</div>
        </div>
        <RangePicker value={rangeKey} onChange={setRangeKey} />
      </div>

      <KpiGrid
        overview={overview.data}
        activeSessions={activeSessions.data}
        pendingInvoices={pendingInvoices.data}
      />

      <div className="grid gap-3.5" style={{ gridTemplateColumns: '3fr 2fr' }}>
        <Card header="الإيرادات حسب الساعة" sub={hourlySub}>
          <HourlyRevenueChart data={hourly.data} />
        </Card>
        <Card header="توزيع الإيراد" sub="حسب القسم">
          <RevenueDonut data={donut.data} />
        </Card>
      </div>

      <div className="grid gap-3.5" style={{ gridTemplateColumns: '3fr 2fr' }}>
        <Card header="آخر الفواتير" sub="جميع نقاط البيع">
          <LatestInvoicesTable data={latestInvoices.data} />
        </Card>
        <Card header="أكثر الأطفال إنفاقاً" sub="اليوم">
          <TopSpendersList data={topSpenders.data} />
        </Card>
      </div>

      <AlertsPanel
        activeSessions={activeSessions.data}
        pendingInvoices={pendingInvoices.data}
        lowStock={lowStock.data}
      />

      <PendingInvoicesPanel data={pendingInvoices.data} />
    </div>
  );
}
